import { isSellable, useSiteDates } from '@/components/checkin/deskRules';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, BedDouble, CornerDownLeft, CreditCard, DoorOpen, Search, Users } from 'lucide-react';
import {
  PERMISSIONS,
  getApiClient,
  normalizeLocale,
  queryKeys,
  useActiveAlarms,
  useAuthStore,
  usePermissions,
  useRooms,
  useTodayStays,
  useTranslation,
} from '@bms/common';
import type { AccessCard, CardListFilters, VisitListFilters } from '@bms/common';
import { Dialog, DialogContent, DialogDescription, DialogTitle, Input, Kbd, cn, useInputsChanged } from '@bms/common/ui';
import { WithGuestKeyCounts, type GuestKeyCounts } from '@/components/desk/GuestKeyCounts';
import { closeDeskSearch, openDeskSearch, useDeskSearch, type DeskSearchRequest } from './deskSearchStore';
import { createDeskFormat } from './deskFormat';
import {
  GROUP_LABEL_KEYS,
  searchDesk,
  tappedCardGroups,
  uniqueStays,
  type DeskAction,
  type DeskIcon,
  type DeskResult,
  type DeskSources,
} from './deskResults';

const ICONS: Record<DeskIcon, typeof Search> = { stay: BedDouble, room: DoorOpen, card: CreditCard, visit: Users };

/** The holder search runs on the server; wait for the desk to stop typing. */
const CARD_SEARCH_DEBOUNCE_MS = 200;
const CARD_SEARCH_MIN_CHARS = 2;
/** Visits searched: from yesterday (one still in the building) to two weeks out. */
const VISIT_WINDOW_BEFORE_MS = 86_400_000;
const VISIT_WINDOW_AFTER_MS = 14 * 86_400_000;

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), ms);
    return () => window.clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

/**
 * The desk's one search (audit P5, UX-22): rooms, guests in house, cards and
 * visitors in one list, on Ctrl+K, the header field, and a card tap (which
 * opens it with the card preselected). Mounted once, in `MainLayout`.
 * ↑↓ move, ↵ runs the row's action, Tab cycles the row's actions, Esc closes.
 */
export function DeskPalette() {
  const { t } = useTranslation();
  const { open, request } = useDeskSearch();

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && !event.altKey && !event.shiftKey && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        openDeskSearch();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) closeDeskSearch(); }}>
      <DialogContent
        hideClose
        className="top-[12%] flex max-h-[76vh] translate-y-0 flex-col gap-0 overflow-hidden p-0 sm:max-w-[560px]"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          (event.currentTarget as HTMLElement).querySelector('input')?.focus();
        }}
      >
        <DialogTitle className="sr-only">{t('search.title')}</DialogTitle>
        <DialogDescription className="sr-only">{t('search.placeholder')}</DialogDescription>
        {/* A new request (typed text, another tap) starts the body afresh. */}
        {open && <PaletteBody key={request.seq} request={request} />}
      </DialogContent>
    </Dialog>
  );
}

function PaletteBody({ request }: { request: DeskSearchRequest }) {
  const [query, setQuery] = useState(request.query);
  const siteCode = useAuthStore((state) => state.currentSiteCode);
  const permissions = usePermissions();
  const canReadCards = permissions.has(PERMISSIONS.CARDS_READ);
  const canReadVisitors = permissions.has(PERMISSIONS.VISITORS_READ);
  const canWriteStay = permissions.has(PERMISSIONS.GRMS_WRITE);

  // The board's own reads, so an open Desk answers from its cache.
  const rooms = useRooms(undefined, { fields: 'board' });
  const stays = useTodayStays();
  const alarms = useActiveAlarms();

  // "Check in" goes on a room only once the open alarms are known: an alarm
  // keeps a room off sale (A1), and an unread list is not an empty one.
  const checkInRooms = useMemo(() => {
    if (!canWriteStay || !alarms.isSuccess) return undefined;
    const openAlarmRooms = new Set((alarms.data ?? [])
      .filter((alarm) => alarm.status === 'active' || alarm.status === 'acknowledged')
      .map((alarm) => alarm.roomCode));
    return new Set((rooms.data ?? []).filter((room) => isSellable(room, openAlarmRooms)).map((room) => room.roomCode));
  }, [canWriteStay, alarms.isSuccess, alarms.data, rooms.data]);

  const holder = useDebounced(query.trim(), CARD_SEARCH_DEBOUNCE_MS);
  const cardFilters: CardListFilters | null = holder.length >= CARD_SEARCH_MIN_CHARS ? { holderName: holder, limit: 20 } : null;
  const cards = useQuery({
    queryKey: queryKeys.accessCards(siteCode ?? '', cardFilters ?? {}),
    queryFn: () => getApiClient().getAccessCards(cardFilters ?? undefined),
    enabled: !!siteCode && canReadCards && cardFilters != null,
  });

  const [visitWindow] = useState<VisitListFilters>(() => {
    const now = Date.now();
    return {
      from: new Date(now - VISIT_WINDOW_BEFORE_MS).toISOString(),
      to: new Date(now + VISIT_WINDOW_AFTER_MS).toISOString(),
      limit: 200,
    };
  });
  const visits = useQuery({
    queryKey: queryKeys.visits(siteCode ?? '', visitWindow),
    queryFn: () => getApiClient().getVisits(visitWindow),
    enabled: !!siteCode && canReadVisitors,
  });

  const sources: Omit<DeskSources, 'keyCounts'> = {
    rooms: rooms.data,
    stays: uniqueStays(stays.data),
    // Answers for an older query are not answers for this one.
    cards: cardFilters && holder === query.trim() ? cards.data : undefined,
    visits: visits.data?.visits,
    checkInRooms,
  };
  const searching = (cardFilters != null && cards.isFetching) || holder !== query.trim();

  return (
    <WithGuestKeyCounts>
      {(keyCounts) => (
        <PaletteView
          query={query}
          onQueryChange={setQuery}
          tappedCard={request.card}
          sources={sources}
          keyCounts={keyCounts}
          timezone={stays.data?.timezone}
          searching={searching}
        />
      )}
    </WithGuestKeyCounts>
  );
}

interface PaletteViewProps {
  query: string;
  onQueryChange: (query: string) => void;
  tappedCard: AccessCard | null;
  sources: Omit<DeskSources, 'keyCounts'>;
  keyCounts: GuestKeyCounts | undefined;
  timezone?: string;
  searching: boolean;
}

function PaletteView({ query, onQueryChange, tappedCard, sources, keyCounts, timezone, searching }: PaletteViewProps) {
  const { t, i18n } = useTranslation();
  const { options } = useSiteDates();
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(0);
  const [actionIndex, setActionIndex] = useState(0);
  const listRef = useRef<HTMLDivElement | null>(null);

  const locale = normalizeLocale(i18n.language);
  const groups = useMemo(() => {
    const fmt = createDeskFormat(t, locale, options.timeZone ?? timezone, new Date(), options.format);
    const all: DeskSources = { ...sources, keyCounts };
    return query.trim() || !tappedCard ? searchDesk(query, all, fmt) : tappedCardGroups(tappedCard, all, fmt);
    // `sources` is rebuilt every render; its members are the stable inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, tappedCard, sources.rooms, sources.stays, sources.cards, sources.visits, keyCounts, timezone, locale, t, options.timeZone, options.format]);
  const items = useMemo(() => groups.flatMap((group) => group.items), [groups]);
  const active: DeskResult | undefined = items[Math.min(activeIndex, Math.max(items.length - 1, 0))];
  const activeAction = active?.actions[Math.min(actionIndex, active.actions.length - 1)];

  // A new search starts at the top.
  const queryChanged = useInputsChanged([query]);
  if (queryChanged && (activeIndex !== 0 || actionIndex !== 0)) {
    setActiveIndex(0);
    setActionIndex(0);
  }

  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>('[aria-selected="true"]')?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex]);

  const run = (action: DeskAction | undefined) => {
    if (!action) return;
    closeDeskSearch();
    navigate(action.to);
  };

  const moveTo = (index: number) => {
    setActiveIndex(index);
    setActionIndex(0);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const count = items.length;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      moveTo(count === 0 ? 0 : (activeIndex + 1) % count);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      moveTo(count === 0 ? 0 : (activeIndex - 1 + count) % count);
    } else if (event.key === 'Home') {
      event.preventDefault();
      moveTo(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      moveTo(Math.max(count - 1, 0));
    } else if (event.key === 'Tab') {
      // Tab walks the active row's actions instead of leaving the palette.
      event.preventDefault();
      const total = active?.actions.length ?? 0;
      if (total > 1) setActionIndex((index) => (index + (event.shiftKey ? total - 1 : 1)) % total);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      run(activeAction);
    }
  };

  let emptyText: string | null = null;
  if (items.length === 0) {
    if (!query.trim()) emptyText = t('search.empty');
    else if (searching) emptyText = t('search.loading');
    else emptyText = t('search.noMatches', { query: query.trim() });
  }

  return (
    <>
      <div className="border-b border-border p-2">
        <Input
          icon={Search}
          role="combobox"
          aria-label={t('search.inputAria')}
          aria-expanded="true"
          aria-controls="desk-palette-list"
          aria-activedescendant={active ? `desk-palette-${active.id}` : undefined}
          aria-autocomplete="list"
          autoComplete="off"
          placeholder={t('search.placeholder')}
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          onKeyDown={onKeyDown}
          className="[&_input]:border-0 [&_input]:shadow-none [&_input]:focus-visible:ring-0"
        />
      </div>
      <div ref={listRef} id="desk-palette-list" role="listbox" aria-label={t('search.title')} className="min-h-0 flex-1 overflow-y-auto p-1">
        {emptyText ? (
          <p className="px-3 py-6 text-center text-body text-muted-foreground">{emptyText}</p>
        ) : groups.map((group) => (
          <div key={group.key} role="group" aria-labelledby={`desk-palette-group-${group.key}`}>
            <p id={`desk-palette-group-${group.key}`} className="px-2 pb-1 pt-2 text-micro font-semibold uppercase tracking-micro text-muted-foreground">
              {t(GROUP_LABEL_KEYS[group.key])}
            </p>
            {group.items.map((item) => {
              const Icon = ICONS[item.icon];
              const isActive = item.id === active?.id;
              return (
                <div
                  key={item.id}
                  id={`desk-palette-${item.id}`}
                  role="option"
                  aria-selected={isActive}
                  onMouseEnter={() => { if (!isActive) moveTo(items.indexOf(item)); }}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => run(isActive ? activeAction : item.actions[0])}
                  className={cn(
                    'flex cursor-pointer flex-wrap items-center gap-x-2 gap-y-1 rounded-md px-2 py-1.5 text-ui text-foreground sm:flex-nowrap',
                    isActive && 'bg-accent text-accent-foreground',
                  )}
                >
                  <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate">
                      <span className="font-semibold">{item.title}</span>
                      {item.detail && <span className="text-muted-foreground"> · {item.detail}</span>}
                    </p>
                    {item.caption && <p className="truncate text-caption text-muted-foreground">{item.caption}</p>}
                  </div>
                  {isActive ? (
                    // On a phone the chips take their own line, so the title keeps its width.
                    <span className="flex shrink-0 items-center gap-1 max-sm:basis-full max-sm:pl-6">
                      {item.actions.map((action) => (
                        <button
                          key={action.to}
                          type="button"
                          tabIndex={-1}
                          onClick={(event) => { event.stopPropagation(); run(action); }}
                          className={cn(
                            'rounded px-1.5 py-0.5 text-caption',
                            action === activeAction ? 'bg-background font-medium text-foreground shadow-xs' : 'text-muted-foreground hover:text-foreground',
                          )}
                        >
                          {t(action.labelKey)}
                        </button>
                      ))}
                    </span>
                  ) : (
                    <span className="hidden shrink-0 text-caption text-muted-foreground sm:inline">{t(item.actions[0].labelKey)}</span>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3 border-t border-border px-3 py-2 text-caption text-muted-foreground">
        <span className="flex items-center gap-1">
          <Kbd><ArrowUp className="size-3" aria-hidden="true" /></Kbd>
          <Kbd><ArrowDown className="size-3" aria-hidden="true" /></Kbd>
          {t('search.hintMove')}
        </span>
        <span className="flex items-center gap-1">
          <Kbd><CornerDownLeft className="size-3" aria-hidden="true" /></Kbd>
          {t('search.hintOpen')}
        </span>
        <span className="flex items-center gap-1">
          <Kbd>{t('search.tabKey')}</Kbd>
          {t('search.hintActions')}
        </span>
        <span className="flex items-center gap-1">
          <Kbd>{t('search.escape')}</Kbd>
        </span>
      </div>
    </>
  );
}
